import React, { useMemo, memo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Brush,
} from 'recharts';
import { Box, Typography, Paper } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

// Converte velocità m/day in mm/anno formattata
const formatRate = (v) => (v * 1000 * 365.25).toFixed(1);

// Formatta data/ora in UTC
const formatUTC = (isoString, formatStr) => {
  const date = parseISO(isoString);
  // Aggiungi offset per compensare la conversione locale e mostrare UTC
  const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  return format(utcDate, formatStr, { locale: it });
};

// Formattazione tooltip (memoizzato) - orari in UTC
const CustomTooltip = memo(({ active, payload, label, unit = 'mm' }) => {
  if (active && payload && payload.length) {
    return (
      <Paper sx={{ p: 1.5, maxWidth: 250 }}>
        <Typography variant="caption" color="text.secondary">
          {formatUTC(label, 'dd MMM yyyy HH:mm')} UTC
        </Typography>
        {payload.map((entry, index) => (
          <Box
            key={index}
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 0.5 }}
          >
            <Typography variant="body2" sx={{ color: entry.color }}>
              {entry.name}:
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {entry.value?.toFixed(2)} {unit}
            </Typography>
          </Box>
        ))}
      </Paper>
    );
  }
  return null;
});

// Componente principale
function TimeSeriesChart({
  data,
  title,
  series = [], // [{ dataKey: 'delta_north', name: 'Nord', color: '#4caf50' }]
  xAxisKey = 'timestamp',
  unit = 'mm',
  height = 300,
  showBrush = true,
  referenceLines = [], // [{ y: 0, stroke: '#999', label: 'Baseline' }]
  yDomain = 'auto',
}) {
  // Formatta dati per il grafico
  const chartData = useMemo(() => {
    if (!data || !data.timestamps) return [];

    return data.timestamps.map((time, index) => {
      const point = { timestamp: time };
      series.forEach((s) => {
        const values = data[s.dataKey];
        if (values && values[index] !== undefined) {
          // Converti con scaleFactor o in mm se necessario
          point[s.dataKey] = s.scaleFactor ? values[index] * s.scaleFactor
            : s.convertToMm ? values[index] * 1000 : values[index];
        }
      });
      return point;
    });
  }, [data, series]);

  // Formatta etichette asse X con orario UTC
  const formatXAxis = (tickItem) => {
    try {
      return formatUTC(tickItem, 'dd/MM HH:mm');
    } catch {
      return tickItem;
    }
  };

  if (!chartData.length) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          borderRadius: 2,
        }}
      >
        <Typography color="text.secondary">Nessun dato disponibile</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height }}>
      {title && (
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          {title}
        </Typography>
      )}
      <ResponsiveContainer width="100%" height={title ? height - 30 : height}>
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey={xAxisKey}
            tickFormatter={formatXAxis}
            tick={{ fontSize: 11 }}
            stroke="#999"
          />
          <YAxis
            domain={yDomain}
            tick={{ fontSize: 11 }}
            stroke="#999"
            tickFormatter={(value) => value.toFixed(1)}
            label={{
              value: unit,
              angle: -90,
              position: 'insideLeft',
              style: { fontSize: 11, fill: '#666' },
            }}
          />
          <Tooltip content={<CustomTooltip unit={unit} />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />

          {/* Reference lines */}
          {referenceLines.map((ref, index) => (
            <ReferenceLine
              key={index}
              y={ref.y}
              stroke={ref.stroke || '#999'}
              strokeDasharray={ref.dashed ? '5 5' : undefined}
              label={ref.label}
            />
          ))}

          {/* Data series */}
          {series.map((s) => (
            <Line
              key={s.dataKey}
              type="monotone"
              dataKey={s.dataKey}
              name={s.name}
              stroke={s.color}
              strokeWidth={s.thin ? 1.5 : 2}
              strokeDasharray={s.dashed ? '8 4' : undefined}
              dot={false}
              activeDot={s.dashed ? false : { r: 4 }}
              legendType={s.dashed ? 'plainline' : 'line'}
            />
          ))}

          {/* Brush per zoom */}
          {showBrush && chartData.length > 2 && (
            <Brush
              dataKey={xAxisKey}
              height={30}
              stroke="#1976d2"
              tickFormatter={formatXAxis}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

// Componente per grafici spostamenti con linee di trend (N/E/U combinati)
export function DisplacementChart({ data, trend, height = 450 }) {
  // Calcola dati con linee di trend
  const enhancedData = useMemo(() => {
    if (!data || !data.timestamps || !trend) return data;

    const times = data.timestamps.map((t) => new Date(t).getTime());
    const meanTime = times.reduce((a, b) => a + b, 0) / times.length;

    const mean = (arr) => {
      const valid = arr.filter((v) => v != null);
      return valid.length ? valid.reduce((a, b) => a + b, 0) / valid.length : 0;
    };
    const meanN = mean(data.delta_north);
    const meanE = mean(data.delta_east);
    const meanU = mean(data.delta_up);

    const slopeN = trend.avg_velocity_north || 0;
    const slopeE = trend.avg_velocity_east || 0;
    const slopeU = trend.avg_velocity_up || 0;

    const MS_PER_DAY = 86400 * 1000;
    return {
      ...data,
      trend_north: times.map((t) => meanN + slopeN * ((t - meanTime) / MS_PER_DAY)),
      trend_east: times.map((t) => meanE + slopeE * ((t - meanTime) / MS_PER_DAY)),
      trend_up: times.map((t) => meanU + slopeU * ((t - meanTime) / MS_PER_DAY)),
    };
  }, [data, trend]);

  const series = [
    { dataKey: 'delta_north', name: 'N', color: '#4caf50', convertToMm: true },
    { dataKey: 'delta_east', name: 'E', color: '#2196f3', convertToMm: true },
    { dataKey: 'delta_up', name: 'U', color: '#ff9800', convertToMm: true },
  ];

  if (trend) {
    series.push(
      { dataKey: 'trend_north', name: `N ${formatRate(trend.avg_velocity_north)}mm/a`, color: '#2e7d32', convertToMm: true, dashed: true, thin: true },
      { dataKey: 'trend_east', name: `E ${formatRate(trend.avg_velocity_east)}mm/a`, color: '#1565c0', convertToMm: true, dashed: true, thin: true },
      { dataKey: 'trend_up', name: `U ${formatRate(trend.avg_velocity_up)}mm/a`, color: '#e65100', convertToMm: true, dashed: true, thin: true },
    );
  }

  return (
    <TimeSeriesChart
      data={enhancedData}
      title="Spostamenti rispetto al riferimento"
      series={series}
      unit="mm"
      height={height}
      referenceLines={[{ y: 0, stroke: '#999', dashed: true }]}
    />
  );
}

// Componente per grafico spostamento totale
export function TotalDisplacementChart({ data, height = 250 }) {
  const series = [
    { dataKey: 'total', name: 'Spostamento totale', color: '#9c27b0', convertToMm: true },
  ];

  return (
    <TimeSeriesChart
      data={data}
      title="Spostamento totale 3D"
      series={series}
      unit="mm"
      height={height}
      yDomain={[0, 'auto']}
    />
  );
}

// Componente per grafico velocità in mm/anno (smoothed con finestra 24h)
export function VelocityChart({ data, height = 250 }) {
  // Ricalcola velocità con finestra 24h per filtrare rumore GNSS
  const smoothedData = useMemo(() => {
    if (!data || !data.timestamps) return data;

    const times = data.timestamps.map((t) => new Date(t).getTime());
    const MS_PER_DAY = 86400 * 1000;
    const WINDOW = 24; // ~24 hourly buckets

    const smoothed = times.map((t, i) => {
      const j = i - WINDOW;
      if (j < 0) return null;

      const timeDiffDays = (times[i] - times[j]) / MS_PER_DAY;
      if (timeDiffDays < 0.5) return null;

      const dN = (data.delta_north[i] || 0) - (data.delta_north[j] || 0);
      const dE = (data.delta_east[i] || 0) - (data.delta_east[j] || 0);
      const dU = (data.delta_up[i] || 0) - (data.delta_up[j] || 0);

      return Math.sqrt(dN * dN + dE * dE + dU * dU) / timeDiffDays;
    });

    return { ...data, total_velocity: smoothed };
  }, [data]);

  const series = [
    { dataKey: 'total_velocity', name: 'Velocità (24h)', color: '#e91e63', scaleFactor: 1000 * 365.25 },
  ];

  return (
    <TimeSeriesChart
      data={smoothedData}
      title="Velocità di spostamento (media mobile 24h)"
      series={series}
      unit="mm/anno"
      height={height}
      referenceLines={[
        { y: 365, stroke: '#ff9800', dashed: true, label: 'Warning' },
        { y: 1825, stroke: '#f44336', dashed: true, label: 'Alarm' },
      ]}
    />
  );
}

// Componente per grafico coordinate
export function CoordinatesChart({ data, type = 'latitude', height = 250 }) {
  const configs = {
    latitude: {
      title: 'Latitudine',
      dataKey: 'latitude',
      color: '#4caf50',
      unit: '°',
    },
    longitude: {
      title: 'Longitudine',
      dataKey: 'longitude',
      color: '#2196f3',
      unit: '°',
    },
    altitude: {
      title: 'Altitudine',
      dataKey: 'altitude',
      color: '#ff9800',
      unit: 'm',
    },
  };

  const config = configs[type];
  const series = [{ ...config, name: config.title }];

  return (
    <TimeSeriesChart
      data={data}
      title={config.title}
      series={series}
      unit={config.unit}
      height={height}
    />
  );
}

export default memo(TimeSeriesChart);
